import get from 'lodash.get';
import merge from 'lodash.merge';
import React from 'react';
import PropTypes from 'prop-types';
import { Route } from 'react-router';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Chart from '../';
import { base } from './config';
import { transform } from './transformers';
import Caret from '../../../../Global/svg/Caret';
import { tooltipTypes, Tooltip } from '../../../../Global/Tooltip';
import Legend from '../../../../Teacher/Legend';
import { legendItems } from '../../../../../constants/legend';
import { getUserData } from '../../../../../services/API';
import pendingImg from "../../../../../images/pending.png"
import BenchmarkCustomLevel from './Cells/BenchmarkCustomLevel';
import AMRToCustomLevelHeader from './Headers/ARMToCustomLevelHeader/AMRToCustomLevelHeader';
import './layoutOverride.scss';
import { useSelector } from 'react-redux';
import { getCutscoreColors } from '../../../util/cutscores';
const RowDecorator = ({ name, level, label, onClick, id, mode, score, isPending, percentile, selectedMetric, selectedLocale, grade, displayScore }) => {
  const {t} = useTranslation("benchmark_report");

  let isDyslexiaMode = (mode == "DYSLEXIA");
  const isEarlyReader = (label === 'Early Reader');
  const assessed = (score && label !== 'Unassessed' && !isEarlyReader) || (isEarlyReader && getUserData().prereaderAudioEnabled);
  const noLevelLabel = getUserData().sorConfig.enableFandP ? `--` : '';
  const shouldDisplayLabel = mode != "DYSLEXIA" && getUserData().sorConfig.enableFandP && level != '' && score;
  let displayedLevelLabel = shouldDisplayLabel ? `Level ${level}` : noLevelLabel;
  if (displayedLevelLabel) displayedLevelLabel = t(displayedLevelLabel);
  const customARMLevelMapping = getUserData()?.sorConfig?.customARMLevelMapping;
  const customARMLevelMappingEnabled = customARMLevelMapping && customARMLevelMapping !== 'none' && selectedMetric?.toLowerCase() === 'arm';
  if (["No Arm Score", "Unassessed"].includes(label)) label = t(label);

  const decoratorStyles = cx({
   'benchmark-chart-row': true,
   'benchmark-chart-row-decorator': true,
   'not-assessed': !assessed,
   'dyslexiaModeRow': isDyslexiaMode,
  });
  const levelLabelStyles = cx({
    'chart-level-label': true,
    'labeled': shouldDisplayLabel
  })
  
  const levelMapping = cx({
    'chart-level-mapping': true,
    'level-mapping-partner': customARMLevelMapping,
    'labeled': true})

  const handleClick = () => {
    if(assessed && label != ""){
      onClick(id)
    }
  }

  return (
    <div onClick={handleClick} className={decoratorStyles}>
      {isPending ? <img className='benchmark-pending-img' src={pendingImg}/> : <div className='benchmark-pending-hide'/>}
      <div className="benchmarkChartStudentName">
        {name}
      </div>
      <div className="chart-label-container">
        <div className="chart-label">{label}</div>
      </div>
      <div className={levelLabelStyles}>{displayedLevelLabel}</div>
      {customARMLevelMappingEnabled && <div className={levelMapping}>
        <BenchmarkCustomLevel metric={selectedMetric} displayScore={displayScore} partner={customARMLevelMapping} />
      </div>}
    </div>
  )
}

RowDecorator.propTypes = {
  name: PropTypes.string,
  level: PropTypes.string,
  label: PropTypes.string,
}

const Benchmark = ({ report, sorts=[], onClick, onScroll, containerWidth, onSortChange, mode="DEFAULT" }) => {
  const { selectedMetric, selectedBenchmarkName, selectedLocale } = report;
  const selectedStatePercentileCuts = useSelector(state => state.reports?.teacher?.selectedStatePercentileCuts);

  const {t} = useTranslation("benchmark_report");

  // TODO deprecate "mode" and use configs exclusively
  let displayCutline = true;
  if(mode == "DYSLEXIA") {
    displayCutline = false;
  }

  const chartClick = (e) => {
    onClick && onClick(e);
  }

  const onChartLoad = (chart) => {
    // make updates once chart has loaded
  }

  const cutoffs = report.cutoffs;
  const cutlines = report.cutlines;
  const { scores, names } = transform(report.scores);

  // estimate the position of cutlines to determine which to include
  let maxScore = Math.max(...scores);
  let cutlineSizerPositions = [];
  for(let i = 0; i < cutlines.length; i++) {
    cutlineSizerPositions[i] = false; // "false" position if this is an invalid cutline
    let value = cutlines[i].value;
    if(value > 0 && value <= maxScore) {
      cutlineSizerPositions[i] = ((containerWidth - 282) * (value / maxScore) * .857482185);
    }
  }

  // TODO refine this by moving prevprev tag by -4 and tag by 4, instead of just moving tag by 8

  // based on a proximity cutoff, generate an array of included cutlines
  let cutlineProximityCutoff = 20;
  let cutlineElements = [];
  for(let i = 0; i < cutlines.length; i++) {
    const cutline = cutlines[i];
    let position = cutlineSizerPositions[i];
    if(position) { // if this cutline hasn't been invalidated, consider including it
      let scaledPositionStyle = {left: 'calc(' + cutline.value / Math.max(...scores)*100 + '% * .857482185)'};

      // determine whether to include the cutline based on other cutline positions
      let includeCutline = true;
      let prevprevPosition = cutlineSizerPositions[(i - 2)];
      let prevPosition = cutlineSizerPositions[(i - 1)];
      if(i > 0) {
        if(prevPosition) {
          if(position - prevPosition < cutlineProximityCutoff) {
            includeCutline = false;
            cutlineSizerPositions[i] = false; // don't get rid of multiple adjacent cutlines
          }
        } else if (i > 1 && prevprevPosition) { // if three lines are close, move one of them
          if(position - prevprevPosition < cutlineProximityCutoff) {
            scaledPositionStyle = {left: 'calc(' + cutline.value / Math.max(...scores)*100 + '% * .857482185 + 8px)'};
            cutlineSizerPositions[i] = cutlineSizerPositions[i] + 8; // don't get rid of multiple adjacent cutlines
          }
        }
      }
      if(includeCutline) {
        const cutlineLabel = cutline.percentile ? t("common:nth_pr", {percentile: cutline.percentile}) : cutline.label;
        cutlineElements.push({
          JSX: <span key={cutline.label} className="benchmark-chart-label" style={scaledPositionStyle}>{cutlineLabel}</span>,
          value: cutline.value,
        });
      }
    }
  }

  cutlineElements.push({
    JSX: <span className="benchmark-header-pr"> {t("common:PR")} <Tooltip type={tooltipTypes.pr} /></span>,
    value: 'PR',
  });

  const colors = getCutscoreColors(selectedStatePercentileCuts);

  // TODO handle variable number of zones depending on report
  let zones = [
    {value: cutoffs.POOR, color: colors.POOR, className: 'zone-poor'},
    {value: cutoffs.AVERAGE, color: colors.AVERAGE, className: 'zone-average'},
    {value: cutoffs.GOOD, color: colors.GOOD, className: 'zone-good'},
    {color: colors.GREAT, className: 'zone-great'},
  ];

  // TODO create a more robust check for benchmark existance
  if(cutoffs.POOR === -1000) {
    zones = [{color:colors.DEFAULT}];
  }
  if(mode == "DYSLEXIA") {
    zones = [
      {value: cutoffs.MEDIUM_RISK, color: colors.GREAT},
      {value: cutoffs.HIGH_RISK, color: colors.AVERAGE},
      {color: colors.POOR},
    ];
  }

  const yAxis = {
    yAxis: {
      plotLines: cutlineElements.map((line) => ({
        color: 'black',
        width: displayCutline ? 3 : 0,
        value: line.value,
        dashStyle: 'longdash',
        zIndex: 300
      })),
      gridLineWidth: 0,
    }
  }

  const xAxis = {
    xAxis: {
      categories: names,
      tickWidth: 0,
      lineWidth: 0
    }
  }

  // calculate height of the Benchmark chart
  const pointWidth = 35;
  const margin = 10;
  const pointPadding = 0.4;
  const chartHeight = margin*2 + ((pointWidth * scores.length) * (1 + pointPadding));

  const styles = {
    chart: {
      height: chartHeight,
      margin: [margin, margin, margin, margin]
    }
  }

  const label = get(report, 'metric.label', '')
  const series = {
    series: [{
      type: 'bar',
      name: label ? `${label} score` : null,
      data: scores,
      dataLabels: {
        enabled: false,
      },
      pointWidth: pointWidth,
      pointPadding: pointPadding
    }]
  }

  const options = merge({}, base, {
    plotOptions: {bar: {zones, events: {click: chartClick}}}
  }, yAxis, xAxis, series, styles)

  let iconClass = "benchmark-chart-header-icon";
  let nameCaretIcon = null;
  let scoreCaretIcon = null;
  if(sorts.length > 0) {
    //Reusing this icon for both headers score and first_name
    const caretIcon = <Caret iconClass={iconClass} down={sorts[0].dir === 'asc' ? true : false} />

    //It's either first_name sorting or score and we only display the icon of the latest clicked header
    if(sorts[0].type == 'first_name') {
      nameCaretIcon = caretIcon;
    }else {
      scoreCaretIcon = caretIcon;
    }
  }

  const readingLevelTooltip = <Tooltip type={tooltipTypes.readingLevel} />
  const riskLevelTooltip = <Tooltip type={tooltipTypes.riskLevel} />

  const benchmarkChartWrapperStyles = cx({
    'benchmark-chart-wrapper': true,
    'col-risk-level': mode == "DYSLEXIA",
    'col-instructional-text-complexity': mode != "DYSLEXIA" && getUserData().sorConfig.enableFandP,
    'col-arm-custom-level-mapping': getUserData().sorConfig.customARMLevelMapping && getUserData().sorConfig.customARMLevelMapping !== 'none' && selectedMetric?.toLowerCase() === 'arm',
  })

  // <div className="benchmark-header-pr">PR{prTooltip}</div>
  return (
    <div className={benchmarkChartWrapperStyles}>
      <Route path="/teacher/reports/type/dyslexia" render={() =>
        <Legend
          items={legendItems}
        />
      }/>
      <div className='benchmark-chart-row benchmark-chart-header'>
        <div className='icon-buffer'/>
        <div className='benchmark-header-name' onClick={() => onSortChange('first_name')}> {t("Student")} {nameCaretIcon} </div>
        <div>{mode == "DYSLEXIA" && <div className='benchmark-header-score' onClick={() => onSortChange('score')}>Risk Level{riskLevelTooltip}  {scoreCaretIcon} </div>} </div>
        {mode != "DYSLEXIA" && getUserData().sorConfig.enableFandP && <div className='benchmark-header-score' onClick={() => onSortChange('score')}>{t("Instructional Text Complexity")}{readingLevelTooltip}  {scoreCaretIcon} </div>}
        <AMRToCustomLevelHeader metric={selectedMetric} />
      </div>
      <div className="benchmark-chart-container" onScroll={onScroll}>
        <div className="benchmark-chart-row-decorators-container">
          {report.scores.map((it, i) => {
            const { names, ids } = transform([it])
            return <RowDecorator onClick={chartClick} key={i} mode={mode} name={names[0]} level={it.GRL} id={ids[0]} label={it.labeledScore} score={it.score} isPending={it.isPending} 
              percentile={it.PR} selectedMetric={selectedMetric} benchmarkName={selectedBenchmarkName} selectedLocale={selectedLocale} grade={it.grade} firstName={it.first_name}
              displayScore={it.displayScore} />
          })}
        </div>
        <div className="benchmark-highchart-container">
          <Chart options={options} callback={onChartLoad} />
        </div>
      </div>
      <div className="benchmark-chart-row benchmark-chart-footer">
        <div className="benchmark-chart-yAxis-labels">
          {displayCutline && cutlineElements.map((cutline, i) => <React.Fragment key={i}>{cutline.JSX}</React.Fragment>)}
        </div>
        <div> </div>
      </div>
    </div>
  )
}

Benchmark.propTypes = {
  // describe + isRequired for validation
  // maybe AJV validators for more precision
  report: PropTypes.object,
}

export default Benchmark